import React from 'react';
import { fmtDuration } from '../../lib/printFileReview/packDetails';
import { usePackDetails } from './usePackDetails';

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

const fmtShort = (value: number | string | null | undefined) => {
  if (value == null) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: string }> = ({ label, value, sub, tone }) => (
  <div className="bg-black/20 rounded-lg px-3 py-2 min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-base font-semibold ${tone || 'text-white'}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-500 truncate">{sub}</div>}
  </div>
);

/** Moderator-only statistics of one pack, from GET /print-files/batches/{id}. */
const PackStatsPanel: React.FC<{ batchId: string; version?: string }> = ({ batchId, version }) => {
  const { details, stats, loading, error, at, reload } = usePackDetails(batchId, true, version);

  if (error) {
    return (
      <div className="flex items-center gap-3 text-sm text-red-300 px-1 py-2">
        Could not load set #{batchId}: {error}
        <button type="button" onClick={() => void reload()} className="px-2.5 py-1 rounded-lg text-xs border-0 bg-slate-700 hover:bg-slate-600 text-white">Retry</button>
      </div>
    );
  }
  if (!details || !stats) return <div className="text-sm text-gray-400 px-1 py-2">{loading ? 'Loading set statistics…' : ''}</div>;

  const s = stats;
  return (
    <div className="flex flex-col gap-2 py-1">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <Stat label="Reviewed" value={`${s.reviewed}/${s.files}`} sub={s.pending ? `${s.pending} pending` : 'all files'} />
        <Stat label="OK" value={s.results.ok} sub={pct(s.results.ok, s.reviewed)} tone="text-green-300" />
        <Stat label="Need design" value={s.results.need_design} sub={pct(s.results.need_design, s.reviewed)} tone="text-red-300" />
        <Stat label="Need CS" value={s.results.need_cs} sub={pct(s.results.need_cs, s.reviewed)} tone="text-amber-300" />
        <Stat label="Manual edits" value={s.manualEdits} sub={`${pct(s.manualEdits, s.reviewed)} of reviewed`} />
        <Stat label="Upscaled chosen" value={s.upscaledChosen} sub={s.upscaledAvailable ? `of ${s.upscaledAvailable} with upscale` : 'no upscaled files'} />
        <Stat label="Auto-reviewed" value={s.autoReviewed} sub="by the pipeline" />
        <Stat label="Changed decisions" value={s.changedDecision} sub="reviewed more than once" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <Stat
          label="Reviewers"
          value={s.reviewers.length ? s.reviewers.map((r) => r.name).join(', ') : '—'}
          sub={s.reviewers.map((r) => `${r.name}: ${r.files}`).join(' · ') || 'no designer reviews yet'}
        />
        <Stat
          label="Review time"
          value={fmtDuration(s.reviewSpanMs)}
          sub={s.firstReviewAt ? `${fmtShort(s.firstReviewAt)} → ${fmtShort(s.lastReviewAt)}` : undefined}
        />
        <Stat label="Per file (median)" value={fmtDuration(s.medianPerFileMs)} sub="breaks over 20 min excluded" />
        <Stat label="Waited for review" value={fmtDuration(s.medianWaitMs)} sub="median, processed → reviewed" />
        <Stat label="Set turnaround" value={fmtDuration(s.turnaroundMs)} sub={s.turnaroundMs != null ? 'created → last review' : 'when done'} />
        <Stat
          label="Pipeline"
          value={s.pipelineIssues ? `${s.pipelineIssues} failed` : 'OK'}
          sub={`${s.reprocessed} reprocessed`}
          tone={s.pipelineIssues ? 'text-red-300' : 'text-green-300'}
        />
      </div>
      <div className="flex items-center gap-3 text-[11px] text-gray-500">
        <span>Orders {fmtShort(details.minOrderDate)} – {fmtShort(details.maxOrderDate)}</span>
        {at && <span>· Loaded {new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
        <button
          type="button"
          onClick={() => void reload()}
          disabled={loading}
          className="ml-auto px-2 py-0.5 rounded text-[11px] border-0 bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
    </div>
  );
};

export default PackStatsPanel;
